import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQueryWithReauth';
import {
  FollowReportRolePrefix,
  FollowReportScheduleUpdateBody,
  GenerateFollowReportBody,
  GetFollowReportSchedulesResponse,
  GetFollowReportsParams,
  GetFollowReportsResponse,
} from './models';

const FOLLOW_REPORTS_TAG = 'follow_reports';
const FOLLOW_REPORT_SCHEDULES_TAG = 'follow_report_schedules';

export const followReportsApi = createApi({
  reducerPath: 'follow_reports',
  baseQuery: baseQueryWithReauth,
  tagTypes: [FOLLOW_REPORTS_TAG, FOLLOW_REPORT_SCHEDULES_TAG],
  endpoints: (build) => ({
    getFollowReportSchedules: build.query<
      GetFollowReportSchedulesResponse,
      { role: FollowReportRolePrefix }
    >({
      query: ({ role }) => ({
        url: `/${role}/follow-reports/schedules`,
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: FOLLOW_REPORT_SCHEDULES_TAG, id: 'LIST' }],
    }),

    updateFollowReportSchedule: build.mutation<
      { data: import('./models').FollowReportSchedule },
      {
        role: FollowReportRolePrefix;
        type: number;
        body: FollowReportScheduleUpdateBody;
      }
    >({
      query: ({ role, type, body }) => ({
        url: `/${role}/follow-reports/schedules/${type}`,
        method: 'PATCH',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: FOLLOW_REPORT_SCHEDULES_TAG, id: 'LIST' }],
    }),

    generateFollowReport: build.mutation<
      { data: import('./models').FollowReport },
      {
        role: FollowReportRolePrefix;
        type: number;
        body: GenerateFollowReportBody;
      }
    >({
      query: ({ role, type, body }) => ({
        url: `/${role}/follow-reports/generate/${type}`,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: FOLLOW_REPORTS_TAG, id: 'LIST' }],
    }),

    getFollowReports: build.query<
      GetFollowReportsResponse,
      { role: FollowReportRolePrefix } & GetFollowReportsParams
    >({
      query: ({ role, page, filters }) => {
        const params: Record<string, string | number> = {};
        if (page) params.page = page;
        if (filters?.report_type !== undefined)
          params['filters[report_type]'] = filters.report_type;
        if (filters?.frequency !== undefined) params['filters[frequency]'] = filters.frequency;
        return {
          url: `/${role}/follow-reports`,
          params,
          headers: { Accept: 'application/json' },
        };
      },
      providesTags: [{ type: FOLLOW_REPORTS_TAG, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetFollowReportSchedulesQuery,
  useUpdateFollowReportScheduleMutation,
  useGenerateFollowReportMutation,
  useGetFollowReportsQuery,
} = followReportsApi;
